import type {
  AttributeResponse,
  PlayerResponse,
  RecStatusResponse,
} from '@on3/api';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Link } from '@on3/ui-lib/components/Link';
import { Check } from '@on3/ui-lib/components/Svg';
import { useSite } from '@on3/ui-lib/index';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { createUrl } from '@on3/ui-lib/utils/url';
import { format } from 'date-fns';

import styles from './CommittedStatus.module.scss';

interface ICommittedStatusV2 {
  player?: PlayerResponse;
  status: RecStatusResponse;
  sport?: AttributeResponse;
}
export const CommittedStatus = ({
  status,
  player,
  sport,
}: ICommittedStatusV2) => {
  const { classYear } = player || {};

  const { type, committedAsset, date } = status ?? {};
  const { defaultYears, sportsData } = useSite();
  const { footballRecruitingYear, nonFootballRecruitingYear } = defaultYears;
  const recruitingYear =
    sport?.key === 1 ? footballRecruitingYear : nonFootballRecruitingYear;
  const commitDate = date && format(new Date(date), 'M/dd/yy');
  const isSignedOrEnrolled = type === 'Signed' || type === 'Enrolled';

  const convertedRecruitingYear = parseInt(recruitingYear.toString(), 10);

  const commitYear =
    classYear && convertedRecruitingYear >= classYear
      ? convertedRecruitingYear
      : classYear;

  const isIndustrySport = sportsData?.find((sportData) => {
    return slugify(sportData.name) === sport?.name;
  })?.isIndustryRankable;

  const commitLink = isIndustrySport
    ? 'industry-comparison-commits'
    : 'commits';
  const link = createUrl([
    'college',
    committedAsset?.slug,
    sport?.name ?? 'football',
    recruitingYear ?? commitYear,
    commitLink,
  ]);

  return (
    <Link
      className={styles.block}
      data-status-type={type}
      data-ui="player-committed-status"
      href={link}
    >
      <div className={styles.container}>
        <figure>
          <Avatar
            altText={
              committedAsset?.fullName
                ? `${committedAsset.fullName} Logo`
                : 'Team Logo'
            }
            domain={committedAsset?.defaultAsset?.domain}
            height={30}
            source={committedAsset?.defaultAsset?.source}
            teamAvatar
            title={committedAsset?.fullName}
            type={committedAsset?.defaultAsset?.type}
            width={30}
          />
        </figure>

        {isSignedOrEnrolled && (
          <div className={styles.checkmarkContainer}>
            <Check className={styles.checkmarkIcon} />
          </div>
        )}
      </div>
      <dl>
        <dt>Status Type</dt>
        <dd>{type}</dd>
        {commitDate && (
          <>
            <dt>Status Date</dt>
            <dd className={styles.date}>{commitDate}</dd>
          </>
        )}
      </dl>
    </Link>
  );
};
